/**
 * BulkImportScreen — drag-and-drop CSV product import.
 *
 * Workflow:
 *   1. Drop a .csv file (or pick via button)
 *   2. Service parses + validates → preview table (first 50 rows + errors)
 *   3. Hit "Import" → chunked atomic writes; live progress bar
 *   4. Final report: inserted / skipped / failed counts
 *
 * No new dependencies — built-in CSV parser in BulkImportService.
 */

import { notifyError, notifySuccess, notifyWarning } from '@/store/useToastStore';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { Button } from '@/components/ui/Button';
import {
  parseCsv,
  validateRows,
  commitRows,
  type ValidatedRow,
  type CommitResult,
} from '@/inventory/BulkImportService';

const TEMPLATE_CSV = [
  'name,sku,barcode,hsn,category,unit,selling_price,purchase_price,mrp,gst_rate,stock,low_stock_alert',
  'Sample Product,SP-001,8901234567890,1234,Beverages,pcs,100,80,120,18,50,10',
  'Another One,SP-002,,,,pcs,250,200,250,12,20,5',
].join('\n');

export function BulkImportScreen() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);

  const [fileName, setFileName] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState<ValidatedRow[]>([]);
  const [parseErrors, setParseErrors] = useState<{ row: number; message: string }[]>([]);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<CommitResult | null>(null);

  const handleFile = useCallback(async (file: File) => {
    setFileName(file.name);
    setRows([]);
    setParseErrors([]);
    setResult(null);
    setParsing(true);
    try {
      const text = await file.text();
      const grid = parseCsv(text);
      const v = validateRows(grid);
      setRows(v.rows);
      setParseErrors(v.errors);
    } catch (err) {
      setParseErrors([{ row: 0, message: `Could not read file: ${(err as Error).message}` }]);
    } finally {
      setParsing(false);
    }
  }, []);

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) void handleFile(file);
  }

  async function startImport() {
    if (!activeStoreId || !merchantId) return;
    if (rows.length === 0) return;
    setImporting(true);
    setProgress({ done: 0, total: rows.length });
    setResult(null);
    try {
      const r = await commitRows(rows, { storeId: activeStoreId, merchantId }, (done, total) =>
        setProgress({ done, total }),
      );
      setResult(r);
      if (r.skipped > 0 || r.errors.length > 0) {
        notifyWarning(`Imported ${r.inserted}; ${r.skipped} skipped and ${r.errors.length} with errors — see the list below.`);
      } else {
        notifySuccess(`Imported ${r.inserted} product${r.inserted === 1 ? '' : 's'}.`);
      }
    } finally {
      setImporting(false);
    }
  }

  function downloadTemplate() {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'product-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (!activeStoreId || !merchantId) {
    return <div className="text-fg-secondary text-body">No active store. Sign in again.</div>;
  }

  const blocking = parseErrors.some((e) => e.row === 0);
  const canImport = !blocking && rows.length > 0 && !importing;

  return (
    <div className="flex h-full flex-col gap-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-md p-1.5 text-fg-tertiary hover:bg-bg-subtle hover:text-fg-primary"
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-display text-fg-primary">Bulk import</h1>
            <p className="mt-1 text-body text-fg-secondary">Upload a CSV to add many products at once.</p>
          </div>
        </div>
        <Button variant="secondary" onClick={downloadTemplate}>
          <FileText size={14} /> Download template
        </Button>
      </div>

      {/* Drop zone */}
      <label
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-stroke-hairline/30 bg-bg-surface px-6 py-12 text-center transition-colors hover:border-stroke-hairline/50 hover:bg-bg-subtle cursor-pointer"
      >
        <Upload size={28} className="text-fg-tertiary" />
        <div>
          <div className="text-body text-fg-primary">Drop a CSV file here, or click to browse</div>
          <div className="mt-0.5 text-label text-fg-tertiary">
            Required columns: <span className="font-mono">name</span>, <span className="font-mono">selling_price</span>
          </div>
        </div>
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={onPick} />
      </label>

      {fileName && (
        <div className="flex items-center gap-2 text-body text-fg-secondary">
          <FileText size={14} />
          <span>{fileName}</span>
          {parsing && <span className="text-label text-fg-tertiary">parsing…</span>}
        </div>
      )}

      {/* Errors */}
      {parseErrors.length > 0 && (
        <div className="rounded-md border border-warning/20 bg-warning/10 px-4 py-3">
          <div className="flex items-center gap-2 text-body text-warning-ink">
            <AlertTriangle size={14} />
            <span>{parseErrors.length} validation issue{parseErrors.length !== 1 ? 's' : ''}</span>
          </div>
          <ul className="mt-2 max-h-40 list-disc space-y-1 overflow-auto pl-5 text-label text-fg-secondary">
            {parseErrors.slice(0, 50).map((e, i) => (
              <li key={i}>
                {e.row > 0 ? `Row ${e.row}: ` : ''}{e.message}
              </li>
            ))}
            {parseErrors.length > 50 && (
              <li className="italic">…and {parseErrors.length - 50} more</li>
            )}
          </ul>
        </div>
      )}

      {/* Preview */}
      {rows.length > 0 && (
        <div className="flex-1 overflow-hidden flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-body text-fg-secondary">
              {rows.length} valid row{rows.length !== 1 ? 's' : ''} ready to import
            </div>
            <Button onClick={startImport} disabled={!canImport}>
              {importing ? `Importing ${progress.done}/${progress.total}…` : `Import ${rows.length}`}
            </Button>
          </div>

          <div className="flex-1 overflow-auto rounded-md border border-stroke-hairline/10 bg-bg-surface">
            <table className="w-full text-body">
              <thead className="sticky top-0 bg-bg-surface">
                <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">GST</th>
                  <th className="px-3 py-2 text-right">Stock</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 50).map((r, i) => (
                  <tr key={i} className="border-b border-stroke-hairline/5 last:border-0">
                    <td className="px-3 py-2 text-fg-primary">{r.name}</td>
                    <td className="px-3 py-2 text-fg-secondary">{r.sku || '—'}</td>
                    <td className="px-3 py-2 text-fg-secondary">{r.categoryName || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.sellingPrice.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.gstRate}%</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.currentStock}</td>
                  </tr>
                ))}
                {rows.length > 50 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-2 text-center text-label italic text-fg-tertiary">
                      Showing first 50 of {rows.length} rows.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="rounded-md border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
          <div className="flex items-center gap-2 text-body">
            <CheckCircle2 size={14} className="text-success-ink" />
            <span className="text-fg-primary">
              Imported {result.inserted} • Skipped {result.skipped} • Errors {result.errors.length}
            </span>
          </div>
          {result.errors.length > 0 && (
            <ul className="mt-2 max-h-40 list-disc space-y-1 overflow-auto pl-5 text-label text-fg-secondary">
              {result.errors.slice(0, 20).map((e, i) => (
                <li key={i}>Row {e.row}: {e.message}</li>
              ))}
              {result.errors.length > 20 && (
                <li className="italic">…and {result.errors.length - 20} more</li>
              )}
            </ul>
          )}
          <div className="mt-3">
            <Button variant="secondary" onClick={() => navigate('/inventory')}>Back to inventory</Button>
          </div>
        </div>
      )}
    </div>
  );
}
